<script setup lang="ts">
import { ref, watch } from 'vue';
import BasketRow from './BasketRow.vue';
import { useInstitutionStore } from '@entities/institution';
import { useUserProfileStore, formatSelectedDeliveryAddress } from '@entities/user';
import { modalStateEnum, useModalStore } from '@shared/model';


const props = defineProps({
  userData: {
    type: Object,
    required: false,
    default: () => ({
      method_receipt: 'Самовывоз',
      delivery_address: '',
      table_id: 0,
    }),
  },
  deliveryAddressError: {
    type: Object,
    required: false,
    default: null,
  },
});
const emit = defineEmits(['updateUserData']);
const storeModal = useModalStore();
const storeUserProfile = useUserProfileStore();
const storeInstitution = useInstitutionStore();
const tabs = [
  { key: 'delivery', label: 'Доставка' },
  { key: 'takeaway', label: 'С собой' },
  { key: 'dine_in', label: 'Ем в зале' },
];

const validKeys = new Set(tabs.map((tab) => tab.key));
const selected = ref(
  validKeys.has(props.userData.method_receipt) ? props.userData.method_receipt : 'delivery',
);

const form = ref({
  method_receipt: props.userData.method_receipt,
  delivery_address: props.userData.delivery_address || '',
  table_id: props.userData.table_id,
  address_institution: props.userData.address_institution || '',
});

watch(
  () => props.userData.delivery_address,
  (val) => {
    if (form.value.delivery_address !== val) {
      form.value.delivery_address = val || '';
    }
  },
);

watch(
  () => props.userData.table_id,
  (val) => {
    form.value.table_id = val;
  },
);
watch(
  () => props.userData.address_institution,
  (val) => {
    form.value.address_institution = val || '';
  },
);

watch(
  () => ({ ...form.value }),
  (val) => {
    emit('updateUserData', val);
  },
);

watch(
  () => storeInstitution.selectedBranch,
  (branch) => {
    if (branch) {
      form.value.address_institution = branch.address;
      emit('updateUserData', form.value);
    }
  },
  { immediate: true },
);

// Флаг для отслеживания программного изменения таба (когда меняем извне — модалку не открываем)
const isProgrammaticChange = ref(false);

watch(selected, (newType, oldType) => {
  if (!validKeys.has(newType)) return;
  form.value.method_receipt = newType;
  emit('updateUserData', form.value);
  if (!isProgrammaticChange.value && oldType !== undefined && newType !== oldType) {
    openAddressModal();
  }
  isProgrammaticChange.value = false;
});

// Синхронизация таба, когда метод изменили из store (например, ModalMapYandex)
watch(
  () => props.userData.method_receipt,
  (newType) => {
    if (validKeys.has(newType) && newType !== selected.value) {
      isProgrammaticChange.value = true;
      selected.value = newType;
    }
  },
);

function openAddressModal() {
  storeModal.open(modalStateEnum.ModalMapYandex, {
    from: 'checkout',
    orderType: form.value.method_receipt,
  });
}

// Автовыбор первого адреса, если их подгрузили в профиль, а в форме пусто
watch(
  () => storeUserProfile.getUser.delivery_address,
  (newArr) => {
    if (!form.value.delivery_address && Array.isArray(newArr) && newArr.length) {
      form.value.delivery_address = newArr[0];
      emit('updateUserData', form.value);
    }
  },
);
</script>

<template>
  <UiKitTabs
    v-model="selected"
    :items="tabs"
    variant="solid"
    color="default"
    size="lg"
    full-width
    class="method-receipt-tabs"
  >
    <!-- Слот для доставки -->
    <template #delivery>
      <BasketRow
        clickable
        :variant="storeUserProfile.selectedDeliveryAddress ? 'default' : 'error'"
        @click="openAddressModal"
      >
        <template #icon>
          <UIcon name="i-heroicons-truck" class="w-5 h-5" />
        </template>
        <template v-if="storeUserProfile.selectedDeliveryAddress">
          <span class="basket-row__title">{{
            formatSelectedDeliveryAddress(storeUserProfile.selectedDeliveryAddress, form.delivery_address.address)
          }}</span>
          <span class="basket-row__subtitle">Изменить адрес доставки</span>
        </template>
        <template v-else>
          <span class="basket-row__title text-hui-danger">
            Указать адрес доставки
          </span>
          <span class="basket-row__subtitle">Куда привезти заказ</span>
        </template>
      </BasketRow>
    </template>

    <!-- Слот для самовывоза -->
    <template #takeaway>
      <BasketRow
        clickable
        :variant="storeInstitution.selectedBranch ? 'default' : 'error'"
        @click="openAddressModal"
      >
        <template #icon>
          <UIcon name="i-heroicons-shopping-bag" class="w-5 h-5" />
        </template>
        <template v-if="storeInstitution.selectedBranch">
          <span class="basket-row__title">
            {{ storeInstitution.selectedBranch.address.street }},
            {{ storeInstitution.selectedBranch.address.city }}
          </span>
          <span class="basket-row__subtitle">Изменить ресторан</span>
        </template>
        <template v-else>
          <span class="basket-row__title text-hui-danger">
            Выбрать ресторан для самовывоза
          </span>
          <span class="basket-row__subtitle">Где забрать заказ</span>
        </template>
      </BasketRow>
    </template>

    <!-- Слот для "Ем в зале" -->
    <template #dine_in>
      <BasketRow
        clickable
        :variant="storeInstitution.selectedBranch ? 'default' : 'error'"
        @click="openAddressModal"
      >
        <template #icon>
          <UIcon name="i-heroicons-building-storefront" class="w-5 h-5" />
        </template>
        <template v-if="storeInstitution.selectedBranch">
          <span class="basket-row__title">
            {{ storeInstitution.selectedBranch.address.street }},
            {{ storeInstitution.selectedBranch.address.city }}
          </span>
          <span class="basket-row__subtitle">Изменить ресторан</span>
        </template>
        <template v-else>
          <span class="basket-row__title text-hui-danger">Выбрать ресторан</span>
          <span class="basket-row__subtitle">Где будете обедать</span>
        </template>
      </BasketRow>
    </template>
  </UiKitTabs>
</template>

<style lang="scss" scoped>
// Метод получения заказа: табы из UI-kit (UiKitTabs), контентом занимается BasketRow.
// Стиль/цвета — токены hui-* (см. tokens.css), без хардкода.
.method-receipt-tabs {
  @apply w-full mb-3;

  // Бордер у контейнера табов — как у строк адреса/времени (BasketRow)
  :deep([role='tablist']) {
    @apply border border-hui-divider;
    // Скругление из пресета «Скругления элементов»: табы стоят вплотную к строкам
    // адреса и времени, и разная форма у соседних блоков сразу бросается в глаза.
    // UiKitTabs — компонент админ-кита со своими токенами `--hui-radius-*`,
    // поэтому подменяем здесь, а не пропом.
    border-radius: var(--app-radius-card, 24px);
  }

  :deep([role='tab']) {
    border-radius: calc(var(--app-radius-card, 24px) * 0.7);
  }
}
</style>
